import { createAsyncThunk, createSlice, type UnknownAction } from '@reduxjs/toolkit';

import { isProblemDetailTextOr } from 'src/common/ProblemDetail';

import { productRequirementsConfigService as service } from './product-requirements-config.service';
import type {
    AssignmentRequest,
    CreateGroupRequest,
    CreateRequirementFieldRequest,
    FieldAssignment,
    ProductRequirementGroup,
    ProductSummary,
    UpdateGroupRequest,
    UpdateRequirementFieldRequest,
} from './product-requirements-config.dto';

interface State {
    /** Editor-allowlist verdict — the config UI renders read-only when false. */
    canEdit: boolean;
    products: ProductSummary[];
    groups: ProductRequirementGroup[];
    assignments: FieldAssignment[];
    loading: boolean;
    saving: boolean;
    error: string | null;
}

const initialState: State = {
    canEdit: false,
    products: [],
    groups: [],
    assignments: [],
    loading: false,
    saving: false,
    error: null,
};

export const loadConfig = createAsyncThunk('productRequirementsConfig/load', async () => {
    const [permissions, products, groups, assignments] = await Promise.all([
        service.getPermissions(),
        service.getProducts(),
        service.getGroups(),
        service.getAssignments(),
    ]);
    return { canEdit: permissions.canEdit, products, groups, assignments };
});

/**
 * Every mutation re-reads groups + assignments afterwards so the editor always
 * reflects the server's truth (display_order rewrites, criticality upserts, …)
 * rather than guessing the post-mutation state locally. The `op` closure is
 * captured inside the thunk, so the dispatched action payload stays
 * serializable.
 *
 * Errors go through `rejectWithValue`: the axios layer throws the ProblemDetail
 * body itself, whose `detail` carries the backend's actionable message (e.g.
 * the 409 "delete the child fields first" guards) — `action.error.message`
 * would lose it.
 */
function mutationThunk<Arg>(name: string, op: (arg: Arg) => Promise<unknown>) {
    return createAsyncThunk(`productRequirementsConfig/${name}`, async (arg: Arg, { rejectWithValue }) => {
        try {
            await op(arg);
        } catch (e) {
            return rejectWithValue(isProblemDetailTextOr(e, 'Failed to save changes'));
        }
        const [groups, assignments] = await Promise.all([service.getGroups(), service.getAssignments()]);
        return { groups, assignments };
    });
}

export const createGroup = mutationThunk('createGroup', (req: CreateGroupRequest) => service.createGroup(req));
export const updateGroup = mutationThunk('updateGroup', (a: { groupId: string; req: UpdateGroupRequest }) =>
    service.updateGroup(a.groupId, a.req),
);
export const deleteGroup = mutationThunk('deleteGroup', (groupId: string) => service.deleteGroup(groupId));
export const reorderGroups = mutationThunk('reorderGroups', (orderedGroupIds: string[]) =>
    service.reorderGroups(orderedGroupIds),
);
export const createField = mutationThunk('createField', (a: { groupId: string; req: CreateRequirementFieldRequest }) =>
    service.createField(a.groupId, a.req),
);
export const updateField = mutationThunk(
    'updateField',
    (a: { groupId: string; fieldId: string; req: UpdateRequirementFieldRequest }) =>
        service.updateField(a.groupId, a.fieldId, a.req),
);
export const deleteField = mutationThunk('deleteField', (a: { groupId: string; fieldId: string }) =>
    service.deleteField(a.groupId, a.fieldId),
);
export const reorderFields = mutationThunk(
    'reorderFields',
    (a: { groupId: string; parentFieldId: string | null; orderedFieldIds: string[] }) =>
        service.reorderFields(a.groupId, a.parentFieldId, a.orderedFieldIds),
);
/** Assign or update an assignment's per-product criticality (same PUT upserts). */
export const setAssignment = mutationThunk(
    'setAssignment',
    (a: { productId: string; fieldId: string; req: AssignmentRequest }) =>
        service.assignField(a.productId, a.fieldId, a.req),
);
export const removeAssignment = mutationThunk('removeAssignment', (a: { productId: string; fieldId: string }) =>
    service.unassignField(a.productId, a.fieldId),
);

const mutations = [
    createGroup,
    updateGroup,
    deleteGroup,
    reorderGroups,
    createField,
    updateField,
    deleteField,
    reorderFields,
    setAssignment,
    removeAssignment,
];

interface MutationResult {
    groups: ProductRequirementGroup[];
    assignments: FieldAssignment[];
}

// Match by action-type prefix rather than `isAnyOf(...thunks)`: spreading ten
// async thunks into isAnyOf produces a union TS can't instantiate (TS2589).
const mutationPrefixes = mutations.map((m) => m.typePrefix);
const isMutationPending = (action: UnknownAction) => mutationPrefixes.some((p) => action.type === `${p}/pending`);
const isMutationRejected = (action: UnknownAction) => mutationPrefixes.some((p) => action.type === `${p}/rejected`);
const isMutationFulfilled = (action: UnknownAction) => mutationPrefixes.some((p) => action.type === `${p}/fulfilled`);

const slice = createSlice({
    name: 'productRequirementsConfig',
    initialState,
    reducers: {
        clearError(state) {
            state.error = null;
        },
    },
    // Statements are intentionally NOT chained: chaining accumulates the
    // builder's generic type across the handlers and trips TS2589
    // ("type instantiation is excessively deep").
    extraReducers: (builder) => {
        builder.addCase(loadConfig.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(loadConfig.fulfilled, (state, action) => {
            state.canEdit = action.payload.canEdit;
            state.products = action.payload.products;
            state.groups = action.payload.groups;
            state.assignments = action.payload.assignments;
            state.loading = false;
        });
        builder.addCase(loadConfig.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message ?? 'Failed to load configuration';
        });
        builder.addMatcher(isMutationPending, (state) => {
            state.saving = true;
            state.error = null;
        });
        builder.addMatcher(isMutationFulfilled, (state, action) => {
            const { groups, assignments } = (action as unknown as { payload: MutationResult }).payload;
            state.groups = groups;
            state.assignments = assignments;
            state.saving = false;
        });
        builder.addMatcher(isMutationRejected, (state, action) => {
            state.saving = false;
            // rejectWithValue puts the ProblemDetail text in `payload`; fall back
            // to the thrown error's message for non-HTTP failures.
            const { payload, error } = action as { payload?: unknown; error?: { message?: string } };
            state.error = (typeof payload === 'string' ? payload : null) ?? error?.message ?? 'Failed to save changes';
        });
    },
});

export const { clearError } = slice.actions;
export default slice.reducer;
